import { NormalizedTopic, UniqueTopicIdType } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";
import { LabelTranslation } from "~/types";

// ✅ Initial states
export const initialState: TTopicState = {
    topics: [],
    topic: null,
    selectedTopicIds: [],
    totalSelectedDocs: 0
};

// ✅ Types

// Action type defn
export type TopicAction =
    | { type: SearchActions.SET_TOPIC_ITEMS; payload: NormalizedTopic[] }
    | { type: SearchActions.SET_ACTIVE_TOPIC; payload: NormalizedTopic | null }
    | { type: SearchActions.SET_SELECTED_TOPIC; payload: number }
    | { type: SearchActions.SET_SELECTED_TOPICS; payload: number[] }
    | { type: SearchActions.REMOVE_SELECTED_TOPIC; payload: number }
    | { type: SearchActions.SET_TOPIC_COUNTS; payload: Record<number, number> | null }
    | { type: SearchActions.SET_TOPICS_DOCS_COUNT; payload: number };

// State type defns
export type TTopicState = {
    /** List of normalized Topics `NormalizedTopic[]` */
    topics: NormalizedTopic[];
    /** Active/selected Topic `NormalizedTopic` */
    topic: NormalizedTopic | null;
    /** Selected Topic keys made up of a Topic's `"disclosureId|categoryId|expploreId"` ID string */
    selectedTopicIds: UniqueTopicIdType[];
    /** Total number of documents from all selected Topic(s) */
    totalSelectedDocs: number;
};

// Store type
export type TTopicMethods =
    | "dispatch"
    | "getSelectedTopics"
    | "getTopicsByParentIDs"
    | "getTopicById"
    | "isTopicSelected"
    | "getTopicCount"
    | "getUniqueTopicId"
    | "getTopicName"
    | "reset";

export type TTopicStore = TTopicState & {
    /**
     * Sets the Topics state using a reducer pattern.
     * @param {TopicAction} action - `TopicAction` action to apply.
     * @example
     * ```
     * const dispatch = useTopicStore((state) => state.dispatch)
     * dispatch({ type: SearchActions.SET_TOPIC_ITEMS, payload: topicsList })
     * ```
     */
    dispatch: (action: TopicAction) => void;
    /**
     * Retrieves all selected Topics whose Topic ID is in the `selectedTopicIds[]` array.
     */
    getSelectedTopics: () => NormalizedTopic[];
    /**
     * Retrieves Topics by `disclosureId` and `categoryId`.
     * @param property - The `NormalizedTopic` object containing the `disclosureId` and `categoryId` key-value properties
     * @example const filtered = getTopicsByParentIDs({ disclosureId: 3, categoryId: 4 });
     */
    getTopicsByParentIDs: (
        params: Partial<Pick<NormalizedTopic, "disclosureId" | "categoryId">>
    ) => NormalizedTopic[] | undefined;
    /**
     * Retrieves a Topic by its Topic Id
     * @param topicId Topic Id (Topic.exploreId)
     */
    getTopicById: (topicId: number) => NormalizedTopic | undefined;
    /**
     * Checks if a Topic is selected
     * @param topicId - Topic ID
     */
    isTopicSelected: (topicId: number) => boolean;
    /**
     * Retrieves the total count of a selected Topic
     */
    getTopicCount: (topicId: number) => number;
    /**
     * Retrieves a Topic's disclosureId, categoryId, and its exploreId (topicId) combined into a unique pipe-delimited string
     * eg., `"disclosureId|categoryId|exploreId"`
     */
    getUniqueTopicId: (topicId: number) => UniqueTopicIdType | null;

    getTopicName: (id: number) => LabelTranslation | null;
    /**
     * Resets the store to its initial state
     */
    reset: () => void;
};
